/**
 * ПОЛНЫЙ ПУТЬ: D:\QR\QR-WEB\dashboard\src\components\Business\BusinessQRCodes.js
 * ВЕРСИЯ: 2.0 - PRO UI + SEARCH
 * ДАТА: 20.11.2025
 * СТАТУС: ✅ PRODUCTION READY
 *
 * ВАЖНО:
 * - Основная логика работы с API НЕ изменена
 * - GET:  /api/business/qr-codes
 * - DELETE: /api/business/qr-codes/:id
 */

import React, { useState, useEffect } from 'react';
import './BusinessQRCodes.css';

const API_URL = 'https://qr-money.onrender.com/api';

const BusinessQRCodes = () => {
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('all');
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadQRCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status]);

  const loadQRCodes = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('userId'); // оставляем для совместимости
      const token = localStorage.getItem('authToken');

      const query = new URLSearchParams({
        page,
        limit: 20,
        // Важно: оставляем тот же подход со status, чтобы не ломать бэкенд
        status: status !== 'all' ? status : undefined
      });

      const response = await fetch(`${API_URL}/business/qr-codes?${query}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка загрузки QR кодов');
      }

      const result = await response.json();
      if (result.success) {
        setQrCodes(result.data.items);
        setTotal(result.data.pagination.total);
      } else {
        throw new Error(result.error || 'Ошибка');
      }
    } catch (err) {
      console.error('❌ Error:', err.message);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQR = async (qrId) => {
    if (!window.confirm('Удалить этот QR код?')) return;

    try {
      const token = localStorage.getItem('authToken');

      const response = await fetch(`${API_URL}/business/qr-codes/${qrId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Ошибка удаления');
      }

      const result = await response.json();
      if (result.success) {
        setError('');
        loadQRCodes();
        alert('✅ QR код удалён');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#2ecc71';
      case 'claimed': return '#3498db';
      case 'expired': return '#e74c3c';
      default: return '#95a5a6';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return '✅ Активен';
      case 'claimed': return '📋 Использован';
      case 'expired': return '⏰ Истёк';
      default: return status || '—';
    }
  };

  const handleFilterChange = (value) => {
    setStatus(value);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  const normalizedSearch = search.trim().toLowerCase();

  const filteredQRCodes = qrCodes.filter((qr) => {
    if (!normalizedSearch) return true;

    const id = (qr.id || '').toString().toLowerCase();
    const statusLabel = getStatusLabel(qr.status || '').toLowerCase();
    const amount = qr.amount != null ? String(qr.amount).toLowerCase() : '';

    return (
      id.includes(normalizedSearch) ||
      statusLabel.includes(normalizedSearch) ||
      amount.includes(normalizedSearch)
    );
  });

  const pageSize = 20;
  const isFirstPage = page === 1;
  const isLastPage = page * pageSize >= total;

  if (loading) {
    return (
      <div className="business-qr-codes">
        <div className="loading-spinner">⏳ Загрузка QR‑кодов...</div>
      </div>
    );
  }

  return (
    <div className="business-qr-codes">
      {/* HEADER */}
      <div className="business-qr-header">
        <div>
          <h1>📱 QR‑коды бизнеса</h1>
          <p className="business-qr-subtitle">
            Управляйте своими QR‑кодами: статусы, суммы, сроки действия и удаление.
          </p>
        </div>
        <div className="business-qr-summary">
          <span className="summary-pill">
            Всего QR: <strong>{total}</strong>
          </span>
        </div>
      </div>

      {/* ERROR */}
      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      {/* FILTERS + SEARCH */}
      <div className="qr-controls">
        <div className="filter-buttons">
          <button
            type="button"
            className={`filter-btn ${status === 'all' ? 'active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            Все
          </button>
          <button
            type="button"
            className={`filter-btn ${status === 'active' ? 'active' : ''}`}
            onClick={() => handleFilterChange('active')}
          >
            Активные
          </button>
          <button
            type="button"
            className={`filter-btn ${status === 'claimed' ? 'active' : ''}`}
            onClick={() => handleFilterChange('claimed')}
          >
            Использованные
          </button>
          <button
            type="button"
            className={`filter-btn ${status === 'expired' ? 'active' : ''}`}
            onClick={() => handleFilterChange('expired')}
          >
            Истёкшие
          </button>
        </div>

        <div className="search-box">
          <input
            type="text"
            placeholder="Поиск по ID, сумме или статусу..."
            value={search}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      {/* TABLE / EMPTY STATE */}
      {filteredQRCodes.length > 0 ? (
        <div className="qr-table-wrapper">
          <table className="qr-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Сумма</th>
                <th>Статус</th>
                <th>Создано</th>
                <th>Истекает</th>
                <th style={{ textAlign: 'center' }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredQRCodes.map((qr, idx) => (
                <tr key={qr.id || idx}>
                  <td className="qr-id">
                    {qr.id ? `${qr.id.substring(0, 8)}…` : '—'}
                  </td>
                  <td className="qr-amount">
                    {parseFloat(qr.amount || 0).toFixed(2)} USDT
                  </td>
                  <td>
                    <span
                      className="qr-status"
                      style={{ backgroundColor: getStatusColor(qr.status) }}
                    >
                      {getStatusLabel(qr.status)}
                    </span>
                  </td>
                  <td className="qr-date">
                    {qr.created_at
                      ? new Date(qr.created_at).toLocaleDateString('ru-RU')
                      : '—'}
                  </td>
                  <td className="qr-date">
                    {qr.expires_at
                      ? new Date(qr.expires_at).toLocaleDateString('ru-RU')
                      : '—'}
                  </td>
                  <td>
                    <div className="qr-actions">
                      {qr.status === 'active' && (
                        <button
                          type="button"
                          className="action-btn delete-btn"
                          onClick={() => handleDeleteQR(qr.id)}
                        >
                          🗑️ Удалить
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <h2>📭 QR‑кодов не найдено</h2>
          <p>
            Попробуйте изменить фильтры или создать новый QR на странице
            <br />
            <strong>«Создать QR»</strong>.
          </p>
        </div>
      )}

      {/* PAGINATION */}
      {total > pageSize && (
        <div className="pagination">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={isFirstPage}
          >
            ◀ Назад
          </button>
          <span>Страница {page}</span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={isLastPage}
          >
            Вперёд ▶
          </button>
        </div>
      )}
    </div>
  );
};

export default BusinessQRCodes;
